package com.klantenstop.web.pages.profile

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import com.klantenstop.web.components.H1Heading
import com.klantenstop.web.components.H2Heading
import com.klantenstop.web.components.layout.PageLayout
import com.klantenstop.web.styles.Typography
import com.materialkobweb.components.widgets.DsEditableField
import com.materialkobweb.components.widgets.DsFlexBox
import com.materialkobweb.components.widgets.DsMaterialSymbols
import com.materialkobweb.components.widgets.FilledButton
import com.materialkobweb.components.widgets.FilledTonalButton
import com.materialkobweb.components.widgets.MaterialSymbolType
import com.materialkobweb.styles.MaterialColorVars
import com.materialkobweb.utils.rememberViewModel
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderBottom
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxHeight
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import kotlinx.coroutines.flow.collectLatest
import org.jetbrains.compose.web.attributes.AutoComplete
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.Color
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Text
import com.varabyte.kobweb.compose.ui.graphics.Color as KobwebColor

@Page("/profiel")
@Composable
fun ProfilePage() {
    // Use constant key to maintain ViewModel across navigation
    val viewModel = rememberViewModel { ProfileViewModel() }
    val state by viewModel.state.collectAsState()

    LaunchedEffect(Unit) {
        viewModel.effect.collectLatest {
            when (it) {
                else -> Unit
            }
        }
    }

    ProfilePageContent(
        state = state,
        onEvent = viewModel::sendIntent
    )
}

@Composable
private fun ProfilePageContent(
    state: ProfileContract.State,
    onEvent: (ProfileContract.Intent) -> Unit
) {
    val breakpoint = rememberBreakpoint()
    PageLayout(
        loginRequired = true,
        title = "Profile"
    ) {
        Column(
            modifier = Modifier
                .fillMaxHeight()
                .fillMaxWidth(if (breakpoint > Breakpoint.MD) 80.percent else 90.percent)
                .padding(top = if (breakpoint >= Breakpoint.MD) 58.px else 36.px),
            horizontalAlignment = Alignment.Start
        ) {
            H1Heading(
                modifier = Modifier.fillMaxWidth().margin(bottom = 24.px),
                typography = Typography.HeadlineMedium,
                text = "Mijn profiel",
                color = MaterialColorVars.Primary.value()
            )

            // Verification Banner
            if (state.currentUser?.isVerified == false) {
                VerificationBanner(
                    state = state,
                    onResendClick = { onEvent(ProfileContract.Intent.ResendVerificationEmail) },
                    modifier = Modifier.fillMaxWidth().margin(bottom = 24.px)
                )
            }

            Column(
                modifier = Modifier.fillMaxWidth(),
                verticalArrangement = Arrangement.spacedBy(24.px),
                horizontalAlignment = Alignment.Start
            ) {
                H2Heading(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(bottom = 4.px)
                        .fontWeight(FontWeight.Bold)
                        .borderBottom(2.px, LineStyle.Solid, Color.gray),
                    typography = Typography.TitleLarge,
                    text = "Profiel"
                )

                DsEditableField(
                    modifier = Modifier.fillMaxWidth(if (breakpoint >= Breakpoint.MD) 40.percent else 100.percent),
                    id = "FirstName",
                    label = "Voornaam",
                    placeholder = "Voornaam",
                    value = state.firstName ?: "",
                    onValueChange = { onEvent(ProfileContract.Intent.UpdateFirstName(it)) },
                    backgroundColor = MaterialColorVars.SurfaceContainer.value(),
                    autoComplete = AutoComplete.name
                )

                DsEditableField(
                    modifier = Modifier.fillMaxWidth(if (breakpoint >= Breakpoint.MD) 40.percent else 100.percent),
                    id = "LastName",
                    label = "Achternaam",
                    placeholder = "Achternaam",
                    value = state.lastName ?: "",
                    onValueChange = { onEvent(ProfileContract.Intent.UpdateLastName(it)) },
                    backgroundColor = MaterialColorVars.SurfaceContainer.value(),
                    autoComplete = AutoComplete.name
                )

                DsEditableField(
                    modifier = Modifier.fillMaxWidth(if (breakpoint >= Breakpoint.MD) 40.percent else 100.percent),
                    id = "Email",
                    label = "Email",
                    placeholder = "Email",
                    value = state.email,
                    onValueChange = { onEvent(ProfileContract.Intent.UpdateEmail(it)) },
                    backgroundColor = MaterialColorVars.SurfaceContainer.value(),
                    autoComplete = AutoComplete.email
                )

                FilledButton(
                    modifier = Modifier.margin(top = 16.px),
                    enabled = state.hasChanged,
                    onClick = { onEvent(ProfileContract.Intent.Update) }
                ) {
                    Text("Update")
                }
            }

            Column(
                modifier = Modifier.fillMaxWidth().margin(top = 68.px, bottom = 68.px),
                verticalArrangement = Arrangement.spacedBy(24.px)
            ) {
                Row(
                    modifier = Modifier.fillMaxWidth()
                        .padding(bottom = 4.px).borderBottom(2.px, LineStyle.Solid, Color.gray),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    H2Heading(
                        modifier = Modifier
                            .fontWeight(FontWeight.Bold),
                        typography = Typography.TitleLarge,
                        text = "Wachtwoord"
                    )

                    FilledTonalButton(
                        onClick = { onEvent(ProfileContract.Intent.ToggleChangingPassword) }
                    ) {
                        Text(if (state.isChangingPassword) "Verberg" else "Wijzig wachtwoord")
                    }
                }

                if (!state.isChangingPassword) {
                    DsEditableField(
                        modifier = Modifier.fillMaxWidth(if (breakpoint >= Breakpoint.MD) 40.percent else 100.percent),
                        label = "Wachtwoord",
                        type = InputType.Password,
                        readOnly = true,
                        value = "                              ",
                        onValueChange = {},
                        placeholder = "",
                        backgroundColor = MaterialColorVars.SurfaceContainer.value(),
                        id = "password_placeholder"
                    )
                } else {
                    DsEditableField(
                        modifier = Modifier.fillMaxWidth(if (breakpoint >= Breakpoint.MD) 40.percent else 100.percent),
                        label = "Oud wachtwoord",
                        type = InputType.Password,
                        value = state.oldPassword,
                        onValueChange = { onEvent(ProfileContract.Intent.ChangeOldPassword(it)) },
                        placeholder = "",
                        id = "old_password",
                        backgroundColor = MaterialColorVars.SurfaceContainer.value(),
                        autoComplete = AutoComplete.currentPassword
                    )

                    DsEditableField(
                        modifier = Modifier.fillMaxWidth(if (breakpoint >= Breakpoint.MD) 40.percent else 100.percent),
                        label = "Nieuw wachtwoord",
                        type = InputType.Password,
                        value = state.newPassword,
                        onValueChange = { onEvent(ProfileContract.Intent.ChangeNewPassword(it)) },
                        placeholder = "",
                        id = "new_password",
                        backgroundColor = MaterialColorVars.SurfaceContainer.value(),
                        autoComplete = AutoComplete.newPassword
                    )

                    DsEditableField(
                        modifier = Modifier.fillMaxWidth(if (breakpoint >= Breakpoint.MD) 40.percent else 100.percent),
                        label = "Bevestig nieuw wachtwoord",
                        type = InputType.Password,
                        value = state.confirmNewPassword,
                        onValueChange = { onEvent(ProfileContract.Intent.ChangeConfirmNewPassword(it)) },
                        placeholder = "",
                        id = "confirm_new_password",
                        backgroundColor = MaterialColorVars.SurfaceContainer.value(),
                        autoComplete = AutoComplete.newPassword
                    )

                    FilledButton(
                        onClick = { onEvent(ProfileContract.Intent.UpdatePassword) },
                        enabled = state.validNewPassword
                    ) {
                        Text("Update wachtwoord")
                    }
                }
            }
        }
    }
}

const val MIN_PASSWORD_LENGTH = 8

val String.isPasswordStrong: Boolean
    get() = this.length >= MIN_PASSWORD_LENGTH && this.contains(Regex("[A-Z]"))

@Composable
private fun VerificationBanner(
    state: ProfileContract.State,
    onResendClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    val breakpoint = rememberBreakpoint()

    Row(
        modifier = modifier
            .padding(16.px)
            .backgroundColor(MaterialColorVars.SurfaceContainer.value())
            .borderRadius(16.px)
            .border(1.px, LineStyle.Solid, MaterialColorVars.SurfaceContainerHighest.value()),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(12.px)
    ) {
        DsMaterialSymbols(
            icon = "warning",
            modifier = Modifier.fontSize(if (breakpoint >= Breakpoint.MD) 48.px else 32.px),
            // TODO: Use warning color from color scheme -- Maybe add static warning color to the ColorScheme, maybe just use an extension function
            color = KobwebColor.rgb(0xffa726),
            type = MaterialSymbolType.Rounded,
            fill = true
        )

        val vertical = breakpoint <= Breakpoint.SM

        DsFlexBox(
            flexDirection = if (vertical) FlexDirection.Column else FlexDirection.Row,
            alignItems = if (vertical) AlignItems.Start else AlignItems.Center,
            gap = 32.px
        ) {
            Column(
                modifier = Modifier.fillMaxWidth(),
                verticalArrangement = Arrangement.spacedBy(16.px)
            ) {
                H2Heading(
                    typography = Typography.TitleLarge,
                    text = "Account niet geverifieerd",
                    color = MaterialColorVars.OnSurface.value()
                )

                SpanText(
                    text = "Je account is nog niet geverifieerd. Hierdoor heb je beperkte toegang tot bepaalde functies zoals het schrijven van reviews. Controleer je e-mail voor de verificatielink of klik op de knop hieronder om een nieuwe verificatie-e-mail te ontvangen.",
                    modifier = Modifier.margin(0.px)
                )

                if (state.verificationEmailSent && state.resendCooldownSeconds > 0) {
                    SpanText(
                        text = "✓ Verificatie-e-mail is verzonden! Controleer je inbox.",
                        modifier = Modifier
                            .margin(0.px)
                            // TODO: Use success color from color scheme -- Maybe add static success color to the ColorScheme, maybe just use an extension function
                            .color(KobwebColor.rgb(0x2e7d32))
                            .fontWeight(FontWeight.Bold),
                    )
                }
            }

            Row(
                horizontalArrangement = Arrangement.Start,
                verticalAlignment = Alignment.CenterVertically
            ) {
                FilledButton(
                    onClick = onResendClick,
                    enabled = state.resendCooldownSeconds == 0
                ) {
                    Text(
                        if (state.resendCooldownSeconds > 0)
                            "Wacht ${state.resendCooldownSeconds}s"
                        else
                            "Opnieuw verzenden"
                    )
                }
            }
        }
    }
}

